import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useAuth } from "../../../firebase/AuthContext";
import { getApiUrl } from "../../../config/api";
import FeedbackModal from "./tabs/FeedbackModal";

const FEEDBACK_API = () => `${getApiUrl()}/api/feedback`;

/* --- status vocabulary ------------------------------------------------ */
const STATUSES = {
  pending: {
    label: "Pending",
    dot: "bg-amber-400",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300",
    accent: "border-l-amber-400",
  },
  reviewing: {
    label: "Reviewing",
    dot: "bg-blue-500",
    chip: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
    accent: "border-l-blue-500",
  },
  resolved: {
    label: "Resolved",
    dot: "bg-green-500",
    chip: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",
    accent: "border-l-green-500",
  },
};

/* --- failure categories ---------------------------------------------- */
const CATEGORIES = {
  wrong_answer: "Wrong answer",
  hallucination: "Hallucination",
  missing_knowledge: "Missing knowledge",
  misunderstood: "Misunderstood",
  tone: "Tone / robotic",
  latency: "Latency",
  config: "Config / setup",
  other: "Other",
};

const STAT_STYLES = {
  orange: { border: "border-l-orange-500", chip: "bg-orange-500" },
  amber: { border: "border-l-amber-400", chip: "bg-amber-400" },
  blue: { border: "border-l-blue-500", chip: "bg-blue-500" },
  green: { border: "border-l-green-500", chip: "bg-green-500" },
};

/* --- helpers ---------------------------------------------------------- */
const timeAgo = (ms) => {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const noteCount = (item) =>
  (item.activity || []).filter((a) => a.type === "note").length;

const lastActivityAt = (item) => {
  const ts = (item.activity || []).map((a) => a.timestamp);
  return ts.length ? Math.max(...ts) : item.message_time;
};

const lastStatusChange = (item) =>
  [...(item.activity || [])].reverse().find((a) => a.type === "status") || null;

/* --- stat card -------------------------------------------------------- */
/* Currently unrendered — the stats row above the feedback list is commented
   out. Kept so it can be switched back on without rewriting it. */
// eslint-disable-next-line no-unused-vars
function StatCard({ label, value, color, iconPath, index }) {
  const s = STAT_STYLES[color] || STAT_STYLES.orange;
  return (
    <div
      className={`flex-1 min-w-[150px] bg-white dark:bg-gray-800/90 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 ${s.border} p-5 flex items-center justify-between shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 animate-slideUp`}
      style={{
        animationDelay: `${index * 80}ms`,
        animationFillMode: "backwards",
      }}
    >
      <div>
        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
          {label}
        </p>
        <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
          {value}
        </p>
      </div>
      <div
        className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-white shadow-lg ${s.chip}`}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={iconPath}
          />
        </svg>
      </div>
    </div>
  );
}

/* --- category tag ----------------------------------------------------- */
function CategoryTag({ category, kind }) {
  // General (feedback-form) reports use a free-text category from their own
  // taxonomy (Bug Report, Feature Request, ...), not the AI failure categories.
  const label =
    kind === "general"
      ? category || "Uncategorized"
      : category
        ? CATEGORIES[category]
        : "Uncategorized";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
      <svg
        className="w-3 h-3 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 7h.01M7 3h5a1.99 1.99 0 011.41.59l7 7a2 2 0 010 2.82l-5 5a2 2 0 01-2.82 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
        />
      </svg>
      {label}
    </span>
  );
}

/* --- source tag (thumbs-down vs feedback-form) ------------------------ */
function SourceTag({ kind }) {
  if (kind !== "general") return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
      Form
    </span>
  );
}

/* --- one feedback row (list item) ------------------------------------ */
function FeedbackRow({ item, onOpen }) {
  const st = STATUSES[item.status];
  const notes = noteCount(item);
  const statusChange = lastStatusChange(item);
  return (
    <button
      onClick={onOpen}
      className={`text-left w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 ${st.accent} rounded-xl px-4 sm:px-5 py-3.5 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-all flex items-center gap-4`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${st.chip}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
            {st.label}
          </span>
          <CategoryTag category={item.category} kind={item.kind} />
          <SourceTag kind={item.kind} />
          {notes > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h6m-6 8l-2-2H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-6l-4 4z"
                />
              </svg>
              {notes}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
          “{item.reason}”
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 min-w-0">
            <svg
              className="w-3.5 h-3.5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span className="truncate max-w-[180px]">
              {item.user?.displayName || item.user?.email || "Unknown user"}
            </span>
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <span>
            {statusChange
              ? `${statusChange.author} · ${st.label.toLowerCase()} ${timeAgo(statusChange.timestamp)}`
              : `Reported ${timeAgo(item.message_time)}`}
          </span>
        </p>
      </div>
      <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-orange-500 shrink-0">
        View conversation
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </span>
    </button>
  );
}

/* --- loading / error states ------------------------------------------ */
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="flex gap-1.5">
        {[0, 0.1, 0.2].map((d) => (
          <span
            key={d}
            className="w-2.5 h-2.5 bg-orange-500 rounded-full animate-bounce"
            style={{ animationDelay: `${d}s` }}
          />
        ))}
      </div>
      <span className="text-sm font-medium text-orange-500">
        Loading feedback…
      </span>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="text-center py-16 text-gray-500 dark:text-gray-400">
      <svg
        className="w-12 h-12 mx-auto mb-3 text-red-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v2m0 4h.01M5.07 19H19a2 2 0 001.75-2.97l-6.93-12a2 2 0 00-3.5 0l-6.93 12A2 2 0 005.07 19z"
        />
      </svg>
      <p className="text-sm font-medium">Couldn't load feedback</p>
      <p className="text-xs mt-1">{message}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-1.5 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
      >
        Try again
      </button>
    </div>
  );
}

/* ===================================================================== */
export default function UserFeedback() {
  const { currentUser, getUserProfile } = useAuth();
  const profile = getUserProfile?.();
  const authorName =
    profile?.displayName || profile?.name || profile?.email || "Admin";

  const [language, setLanguage] = useState("en");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const [openId, setOpenId] = useState(null);
  const [conv, setConv] = useState(null); // { loading, error, messages, flaggedAgentId, flaggedUserId }
  const [userHistory, setUserHistory] = useState(null); // { loading, loadingMore, error, items, hasMore } — general reports only
  const historyCursorRef = useRef(null); // {ts, id} of the last loaded history item; drives "load more"
  const [expandedHistorySession, setExpandedHistorySession] = useState(null);
  const [historySessionDetails, setHistorySessionDetails] = useState({}); // sessionId -> { loading, messages, error }

  const token = useCallback(async () => {
    if (!currentUser) throw new Error("Not authenticated");
    return currentUser.getIdToken();
  }, [currentUser]);

  /* ── load the list ──────────────────────────────────────────────── */
  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(FEEDBACK_API(), {
        headers: { Authorization: `Bearer ${await token()}` },
      });
      setItems(res.data?.items || []);
    } catch (e) {
      setError(
        e.response?.data?.error || e.message || "Failed to load feedback.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (currentUser) loadList();
  }, [currentUser, loadList]);

  const counts = useMemo(
    () => ({
      all: items.length,
      pending: items.filter((i) => i.status === "pending").length,
      reviewing: items.filter((i) => i.status === "reviewing").length,
      resolved: items.filter((i) => i.status === "resolved").length,
    }),
    [items],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((i) => filter === "all" || i.status === filter)
      .filter(
        (i) =>
          !q ||
          (i.reason || "").toLowerCase().includes(q) ||
          (i.message || "").toLowerCase().includes(q) ||
          (i.user_id || "").toLowerCase().includes(q) ||
          (i.session_id || "").toLowerCase().includes(q),
      )
      .sort((a, b) => lastActivityAt(b) - lastActivityAt(a));
  }, [items, filter, query]);

  const openItem = items.find((i) => i.feedback_id === openId) || null;

  const replaceItem = (updated) =>
    setItems((prev) =>
      prev.map((i) => (i.feedback_id === updated.feedback_id ? updated : i)),
    );

  /* ── open modal + fetch its conversation ────────────────────────── */
  const openFeedback = useCallback(
    async (feedbackId) => {
      setOpenId(feedbackId);

      // General (feedback-form) reports have no chat session to fetch —
      // everything the modal needs already came in the list response.
      // Instead, pull the reporter's recent conversation history for context.
      const item = items.find((i) => i.feedback_id === feedbackId);
      if (item?.kind === "general") {
        setConv({ loading: false, messages: [] });
        historyCursorRef.current = null;
        setUserHistory({ loading: true });
        try {
          const res = await axios.get(
            `${FEEDBACK_API()}/${feedbackId}/history`,
            { headers: { Authorization: `Bearer ${await token()}` } },
          );
          historyCursorRef.current = res.data?.next_cursor || null;
          setUserHistory({
            loading: false,
            items: res.data?.items || [],
            hasMore: !!res.data?.has_more,
          });
        } catch (e) {
          setUserHistory({
            loading: false,
            error:
              e.response?.data?.error ||
              e.message ||
              "Failed to load conversation history.",
          });
        }
        return;
      }

      setUserHistory(null);
      setConv({ loading: true });
      try {
        const res = await axios.get(
          `${FEEDBACK_API()}/${feedbackId}/conversation`,
          { headers: { Authorization: `Bearer ${await token()}` } },
        );
        setConv({
          loading: false,
          messages: res.data?.messages || [],
          flaggedAgentId: res.data?.flagged_agent_id || null,
          flaggedUserId: res.data?.flagged_user_id || null,
        });
      } catch (e) {
        setConv({
          loading: false,
          error:
            e.response?.data?.error ||
            e.message ||
            "Failed to load conversation.",
        });
      }
    },
    [token, items],
  );

  const closeModal = () => {
    setOpenId(null);
    setConv(null);
    setUserHistory(null);
    historyCursorRef.current = null;
    setExpandedHistorySession(null);
    setHistorySessionDetails({});
  };

  /* ── fetch the next page of a reporter's past conversations ───────── */
  const loadMoreHistory = useCallback(async () => {
    const cursor = historyCursorRef.current;
    if (!openId || !cursor || userHistory?.loadingMore) return;

    setUserHistory((prev) => ({ ...prev, loadingMore: true }));
    try {
      const res = await axios.get(`${FEEDBACK_API()}/${openId}/history`, {
        params: { cursor_ts: cursor.ts, cursor_id: cursor.id },
        headers: { Authorization: `Bearer ${await token()}` },
      });
      historyCursorRef.current = res.data?.next_cursor || null;
      setUserHistory((prev) => ({
        loading: false,
        loadingMore: false,
        items: [...(prev?.items || []), ...(res.data?.items || [])],
        hasMore: !!res.data?.has_more,
      }));
    } catch (e) {
      setUserHistory((prev) => ({
        ...prev,
        loadingMore: false,
        loadMoreError:
          e.response?.data?.error ||
          e.message ||
          "Failed to load more conversations.",
      }));
    }
  }, [openId, token, userHistory?.loadingMore]);

  /* ── expand a past-conversation summary into its full transcript ──── */
  const toggleHistorySession = useCallback(
    async (sessionId) => {
      if (expandedHistorySession === sessionId) {
        setExpandedHistorySession(null);
        return;
      }
      setExpandedHistorySession(sessionId);

      if (
        historySessionDetails[sessionId]?.messages ||
        historySessionDetails[sessionId]?.loading
      )
        return;

      setHistorySessionDetails((prev) => ({
        ...prev,
        [sessionId]: { loading: true },
      }));
      try {
        const res = await axios.get(
          `${FEEDBACK_API()}/${openId}/history/${sessionId}`,
          { headers: { Authorization: `Bearer ${await token()}` } },
        );
        setHistorySessionDetails((prev) => ({
          ...prev,
          [sessionId]: { loading: false, messages: res.data?.messages || [] },
        }));
      } catch (e) {
        setHistorySessionDetails((prev) => ({
          ...prev,
          [sessionId]: {
            loading: false,
            error:
              e.response?.data?.error ||
              e.message ||
              "Failed to load conversation.",
          },
        }));
      }
    },
    [openId, token, expandedHistorySession, historySessionDetails],
  );

  /* ── triage actions (backend-authoritative) ─────────────────────── */
  // Status + category are staged in the modal and saved together in one PATCH.
  const saveAssessment = async (feedbackId, patch) => {
    try {
      const res = await axios.patch(`${FEEDBACK_API()}/${feedbackId}`, patch, {
        headers: { Authorization: `Bearer ${await token()}` },
      });
      if (res.data?.feedback) replaceItem(res.data.feedback);
    } catch (e) {
      alert(e.response?.data?.error || "Failed to save assessment.");
      throw e; // keep the modal's unsaved state so nothing is silently lost
    }
  };

  const addNote = async (feedbackId, text) => {
    try {
      const res = await axios.post(
        `${FEEDBACK_API()}/${feedbackId}/notes`,
        { text },
        { headers: { Authorization: `Bearer ${await token()}` } },
      );
      if (res.data?.feedback) replaceItem(res.data.feedback);
    } catch (e) {
      alert(e.response?.data?.error || "Failed to add note.");
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm("Delete this feedback report? This cannot be undone."))
      return;
    try {
      await axios.delete(`${FEEDBACK_API()}/${feedbackId}`, {
        headers: { Authorization: `Bearer ${await token()}` },
      });
      setItems((prev) => prev.filter((i) => i.feedback_id !== feedbackId));
      closeModal();
    } catch (e) {
      alert(e.response?.data?.error || "Failed to delete feedback.");
    }
  };

  const TABS = [
    { key: "all", label: "All", count: counts.all },
    { key: "pending", label: "Pending", count: counts.pending },
    { key: "reviewing", label: "Reviewing", count: counts.reviewing },
    { key: "resolved", label: "Resolved", count: counts.resolved },
  ];

  return (
    <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg font-sans">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto pt-12 px-4 md:px-8">
          <div className="max-w-4xl mx-auto flex flex-col gap-6">
            {/* header */}
            <div className="mt-10 lg:mt-0 flex flex-col items-center text-center px-4 mb-2">
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 dark:text-white mb-1.5">
                User Feedback Hub
              </h1>
              <p className="text-sm lg:text-base text-gray-600 dark:text-slate-200 max-w-2xl">
                Review the messages users flagged when the AI didn't meet their
                expectations.
              </p>
            </div>

            {/* stats */}
            {/* <div className="flex flex-wrap gap-4">
              <StatCard
                label="Total Feedback"
                value={counts.all}
                color="orange"
                index={0}
                iconPath="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83L3 20l1.17-3.5A7.9 7.9 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
              <StatCard
                label="Pending"
                value={counts.pending}
                color="amber"
                index={1}
                iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <StatCard
                label="Reviewing"
                value={counts.reviewing}
                color="blue"
                index={2}
                iconPath="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
              <StatCard
                label="Resolved"
                value={counts.resolved}
                color="green"
                index={3}
                iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </div> */}

            {/* controls */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-1.5">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setFilter(t.key)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      filter === t.key
                        ? "bg-orange-500 text-white shadow-sm"
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-orange-300"
                    }`}
                  >
                    {t.label}
                    <span
                      className={`ml-1.5 text-xs ${filter === t.key ? "text-orange-100" : "text-gray-400"}`}
                    >
                      {t.count}
                    </span>
                  </button>
                ))}
              </div>

              <div className="relative sm:w-72">
                <svg
                  className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search reason, response, user…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors"
                />
              </div>
            </div>

            {/* list */}
            <div className="flex flex-col gap-2.5 pb-10">
              {loading ? (
                <LoadingState />
              ) : error ? (
                <ErrorState message={error} onRetry={loadList} />
              ) : visible.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 opacity-40"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83L3 20l1.17-3.5A7.9 7.9 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <p className="text-sm font-medium">
                    {items.length === 0
                      ? "No feedback yet"
                      : "No feedback matches your filters"}
                  </p>
                  <p className="text-xs mt-1">
                    {items.length === 0
                      ? "Reports appear here when users flag an AI response."
                      : "Try a different status or clear the search."}
                  </p>
                </div>
              ) : (
                visible.map((item) => (
                  <FeedbackRow
                    key={item.feedback_id}
                    item={item}
                    onOpen={() => openFeedback(item.feedback_id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {openItem && (
        <FeedbackModal
          item={openItem}
          authorName={authorName}
          conversation={conv}
          userHistory={userHistory}
          expandedHistorySessionId={expandedHistorySession}
          historySessionDetails={historySessionDetails}
          onToggleHistorySession={toggleHistorySession}
          onLoadMoreHistory={loadMoreHistory}
          onSaveAssessment={saveAssessment}
          onAddNote={addNote}
          onDelete={deleteFeedback}
          onRetryConversation={() => openFeedback(openItem.feedback_id)}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
